import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useTenant } from '../../contexts/TenantContext';

const IconArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
);
const IconPaste = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
);
const IconUpload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);
const IconLink = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
);
const IconFile = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
);
const IconX = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);

type InputMode = 'paste' | 'upload' | 'url';

export const CreateJobPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authFetch = useAuthFetch();
  const { org } = useTenant();
  const urlImportEnabled = !!org?.settings?.urlImportEnabled;
  const fromGuidedEntry = searchParams.get('entry') === 'guided';

  const [mode, setMode] = useState<InputMode>('paste');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigateToJob = (result: { jdId?: string }) => {
    if (result.jdId) {
      navigate(`/jobs/${result.jdId}`);
    } else {
      navigate('/jobs');
    }
  };

  const handleSaveText = async () => {
    if (!text.trim() || text.trim().length < 20) {
      setError('Please provide a job description (at least 20 characters).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/save-job-description', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail?.message || data?.detail || 'Failed to save');
      }
      navigateToJob(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save job description');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUrl = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please enter a URL.');
      return;
    }
    try {
      new URL(trimmed);
    } catch {
      setError('Please enter a valid URL (e.g. https://...).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/save-job-description', {
        method: 'POST',
        body: JSON.stringify({ url: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail?.message || data?.detail || 'Failed to import from URL');
      }
      navigateToJob(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to import job description from URL');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFile = async () => {
    if (!file) {
      setError('Please select a file.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authFetch('/api/upload-jd-file', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail?.message || data?.detail || 'Failed to upload file');
      }
      navigateToJob(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload job description file');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (mode === 'paste') handleSaveText();
    else if (mode === 'url') handleSaveUrl();
    else handleSaveFile();
  };

  const canSave = () => {
    if (saving) return false;
    if (mode === 'paste') return text.trim().length >= 20;
    if (mode === 'url') return url.trim().length > 0;
    return !!file;
  };

  const handleFileSelect = (selectedFile: File) => {
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.docx') && !name.endsWith('.txt')) {
      setError('Only PDF, DOCX, and TXT files are supported.');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File is too large. Maximum size is 10 MB.');
      return;
    }
    setError(null);
    setFile(selectedFile);
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const tabStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: 'var(--space-2) var(--space-3)',
    border: 'none',
    background: active ? 'var(--accent-subtle)' : 'transparent',
    color: active ? 'var(--accent-color)' : 'var(--text-secondary)',
    fontWeight: active ? 600 : 400,
    fontSize: 'var(--font-size-sm)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div className="v2-page" style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <button className="v2-btn v2-btn-ghost" onClick={() => navigate(fromGuidedEntry ? '/' : '/jobs')}>
          <IconArrowLeft /> Jobs
        </button>
      </div>

      <div className="v2-page-header">
        <h1>New Job</h1>
        <p>Add a job description by pasting text{urlImportEnabled ? ', uploading a file, or importing from a URL' : ' or uploading a file'}.</p>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)', color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>
          {error}
        </div>
      )}

      <div className="v2-card">
        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 'var(--space-1)', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-color)' }}>
          <button style={tabStyle(mode === 'paste')} onClick={() => { setMode('paste'); setError(null); }}>
            <IconPaste /> Paste Text
          </button>
          <button style={tabStyle(mode === 'upload')} onClick={() => { setMode('upload'); setError(null); }}>
            <IconUpload /> Upload File
          </button>
          {urlImportEnabled && (
            <button style={tabStyle(mode === 'url')} onClick={() => { setMode('url'); setError(null); }}>
              <IconLink /> Import URL
            </button>
          )}
        </div>

        <div className="v2-card-body">
          {/* Paste text mode */}
          {mode === 'paste' && (
            <>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Paste the complete job description here..."
                rows={14}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-relaxed)',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {text.length} characters
              </div>
            </>
          )}

          {/* Upload file mode */}
          {mode === 'upload' && (
            <>
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragging ? 'var(--accent-color)' : 'var(--border-color)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-8) var(--space-4)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: dragging ? 'var(--accent-subtle)' : 'var(--bg-primary)',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ color: dragging ? 'var(--accent-color)' : 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>
                  <IconFile />
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                  {dragging ? 'Drop file here' : 'Drag & drop a file here, or click to browse'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  PDF, Word (.docx), or Text (.txt) — up to 10 MB
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                style={{ display: 'none' }}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />
              {file && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                  marginTop: 'var(--space-3)', padding: 'var(--space-3)',
                  background: 'var(--accent-subtle)', borderRadius: 'var(--radius-md)',
                }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', flex: 1 }}>
                    {file.name} <span style={{ color: 'var(--text-muted)' }}>({(file.size / 1024).toFixed(0)} KB)</span>
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}
                    title="Remove file"
                  >
                    <IconX />
                  </button>
                </div>
              )}
            </>
          )}

          {/* URL import mode */}
          {mode === 'url' && (
            <>
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  Job Posting URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://careers.company.com/jobs/..."
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'inherit',
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && canSave()) handleSave(); }}
                />
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Works with Greenhouse, Lever, Workday, and company career pages.
                <br />
                <strong>Note:</strong> LinkedIn, Indeed, and Glassdoor block automated access — for those sites, copy the job description and use the <em>Paste Text</em> tab.
              </div>
            </>
          )}

          {/* Save button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
            <button
              className="v2-btn v2-btn-primary"
              onClick={handleSave}
              disabled={!canSave()}
            >
              {saving ? (
                <><span className="v2-spinner" /> {mode === 'url' ? 'Importing...' : mode === 'upload' ? 'Uploading...' : 'Saving...'}</>
              ) : (
                mode === 'url' ? 'Import Job Description' : mode === 'upload' ? 'Upload & Save' : 'Save Job Description'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
